import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * ScrollToTop — jumps the window to the top of the page whenever the route
 * (pathname) changes. If the URL includes a hash (e.g. /#workshop), we scroll
 * to that in-page anchor instead so anchor-linked nav items still work.
 *
 * Also disables the browser's default scroll-restoration so hard refreshes
 * and back/forward navigations always start at the top instead of jumping
 * back to the previous scroll position.
 */
export default function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  useEffect(() => {
    if (hash) {
      // Wait a tick so the target element has mounted, then scroll to it.
      const id = hash.replace(/^#/, '');
      requestAnimationFrame(() => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'auto', block: 'start' });
        } else {
          window.scrollTo(0, 0);
        }
      });
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }

    // Safety net — Radix UI (dropdowns / dialogs) sometimes leaves
    // `pointer-events: none` stuck on <body> after a menu-triggered
    // navigation, silently blocking every click on the new page until
    // the user reloads. Clear it on every route change so the app
    // remains interactive no matter which primitive fired.
    if (typeof document !== 'undefined' && document.body) {
      if (document.body.style.pointerEvents === 'none') {
        document.body.style.pointerEvents = '';
      }
    }
  }, [pathname, hash]);

  return null;
}
